import { gql } from "@apollo/client";
import { AdminLayout } from "components/admin/admin-layout";
import { AdminSearchBar } from "components/admin/admin-search-bar";
import { Button } from "components/buttons/button";
import currency from "currency.js";
import {
  GetOrdersForAdminPanelQueryVariables,
  Orders,
  Orders_Bool_Exp,
  Order_By,
  Order_Status_Enum,
  useGetOrdersForAdminPanelQuery,
} from "lib/graphql-generated";
import { useInput } from "lib/hooks/use-input";
import { getOrderTotal, parseParam } from "lib/utils";
import Link from "next/link";
import { useRouter } from "next/router";
import { ChangeEvent, useEffect, useState } from "react";
import _ from "lodash";
import querystring from "querystring";
import { DateUtil } from "lib/date";

const GET_ORDERS = gql`
  query GetOrdersForAdminPanel(
    $limit: Int
    $offset: Int
    $conditions: orders_bool_exp
    $orderBy: [orders_order_by!]
  ) {
    orders_aggregate(where: $conditions) {
      aggregate {
        count
      }
    }
    orders(
      limit: $limit
      offset: $offset
      order_by: $orderBy
      where: $conditions
    ) {
      id
      date_created
      status
      custom_line_price
      shipping_option {
        name
        price
      }
      address {
        name
      }
      order_books_aggregate {
        aggregate {
          count
        }
      }
      order_books {
        price
        discount
        quantity
        book_isbn
      }

      subscription_objects {
        id
        subscription {
          id
          price
        }
        subscription_draft_object {
          credits
        }
      }

      order_products {
        price
        discount
        quantity
      }
      vouchers {
        initial_amount
      }
      pif_donations {
        amount
      }
      discount {
        discount_value
        is_fixed_amount
        discounted_books {
          book_isbn
        }
      }
      subscription_objects_aggregate {
        aggregate {
          count
        }
      }
      order_vouchers_for_discount {
        voucher_id
        amount_discounted
      }
      order_books_aggregate {
        aggregate {
          count
        }
      }
      order_products_aggregate {
        aggregate {
          count
        }
      }
    }
  }
`;

type tabName = "Inbox" | "Archived" | "All" | Order_Status_Enum;
const tabs: { name: tabName; query: Orders_Bool_Exp; isMain?: boolean }[] = [
  {
    name: "Inbox",
    isMain: true,
    query: {
      _or: [
        { status: { _eq: Order_Status_Enum.New } },
        { status: { _eq: Order_Status_Enum.Processing } },
        { status: { _eq: Order_Status_Enum.PartiallyFufilled } },
        { status: { _eq: Order_Status_Enum.PreOrder } },
      ],
    },
  },
  {
    name: "Archived",
    isMain: true,
    query: {
      _or: [
        {
          status: { _eq: Order_Status_Enum.Fulfilled },
        },
        { status: { _eq: Order_Status_Enum.Refunded } },
        { status: { _eq: Order_Status_Enum.Cancelled } },
      ],
    },
  },
  {
    name: Order_Status_Enum.New,
    query: { status: { _eq: Order_Status_Enum.New } },
  },
  {
    name: Order_Status_Enum.Processing,
    query: { status: { _eq: Order_Status_Enum.Processing } },
  },
  {
    name: Order_Status_Enum.PartiallyFufilled,
    query: { status: { _eq: Order_Status_Enum.PartiallyFufilled } },
  },
  {
    name: Order_Status_Enum.PreOrder,
    query: { status: { _eq: Order_Status_Enum.PreOrder } },
  },
  {
    name: Order_Status_Enum.InvoiceSent,
    query: { status: { _eq: Order_Status_Enum.InvoiceSent } },
  },
  {
    name: Order_Status_Enum.Fulfilled,
    query: { status: { _eq: Order_Status_Enum.Fulfilled } },
  },
  {
    name: Order_Status_Enum.Refunded,
    query: { status: { _eq: Order_Status_Enum.Refunded } },
  },
  {
    name: Order_Status_Enum.Draft,
    query: { status: { _eq: Order_Status_Enum.Draft } },
  },
  { name: "All", query: {} },
];

export const AllOrders = () => {
  /**
   * Query Strings
   */
  const router = useRouter();
  const pQuery = parseInt(router.query["p"] as string) || 0;
  const statusQuery = parseParam(router.query["status"]);
  const searchQuery = parseParam(router.query["search"]) || "";
  const paginate = Boolean(pQuery) ? pQuery : 0;
  const orderBy: Order_By | "" =
    (parseParam(router.query["order"]) as Order_By) ?? "";
  const limit = 25;
  const offset = Boolean(paginate) ? paginate * limit : 0;
  const [curentTab, setTab] = useState<typeof tabs[number]>(
    tabs.find((t) => t.name === "Inbox")
  );
  const search = useInput<string>(searchQuery);

  const getPathWithQuery = ({
    p,
    status,
    search,
    order,
  }: {
    p?: number;
    status?: string;
    search?: string;
    order?: string;
  } = {}) => {
    const queryString = querystring.encode({
      p: p ?? paginate ?? 0,
      status: status ?? statusQuery ?? "Inbox",
      search: search ?? searchQuery ?? "",
      order: order ?? orderBy ?? "",
    });
    return `${router.pathname}?${queryString}`;
  };

  let variables: GetOrdersForAdminPanelQueryVariables = {
    orderBy:
      orderBy !== ""
        ? [{ id: orderBy as Order_By }, { date_created: Order_By.Desc }]
        : [{ date_created: Order_By.Desc }],
    limit: limit,
    offset: offset,
    conditions: {
      _and: [
        tabs.find((t) =>
          statusQuery ? t.name === statusQuery : t.name === "Inbox"
        ).query,
        {
          _or: [
            {
              address: { contact_email: { _ilike: `%${searchQuery ?? ""}%` } },
            },
            { address: { name: { _ilike: `%${searchQuery ?? ""}%` } } },
            {
              address: {
                account: { email: { _ilike: `%${searchQuery ?? ""}%` } },
              },
            },
            {
              address: {
                account: { display_name: { _ilike: `%${searchQuery ?? ""}%` } },
              },
            },
            {
              order_books: {
                book: { title: { _ilike: `%${searchQuery ?? ""}%` } },
              },
            },
            {
              order_products: {
                product: { title: { _ilike: `%${searchQuery ?? ""}%` } },
              },
            },
          ] as Orders_Bool_Exp[],
        },
      ],
    },
  };

  if (!!parseInt(searchQuery)) {
    variables.conditions._and[1]._or.push({
      id: {
        _eq: parseInt(searchQuery),
      },
    });
  }

  const { data, loading, error, refetch } = useGetOrdersForAdminPanelQuery({
    variables: variables,
    pollInterval: 10000,
  });

  return (
    <>
      <AdminLayout
        page='Orders'
        searchBar={
          <AdminSearchBar
            placeholder='Search by customer name / email'
            onSearch={() =>
              router.replace(
                getPathWithQuery({ search: search.value, status: "All", p: 0 })
              )
            }
            {...search.bind}
          />
        }>
        <article className='space-y-4'>
          <header className='flex flex-col lg:flex-row flex-wrap w-full justify-between'>
            <h1 className='header text-4xl ml-auto mr-auto xl:ml-0 xl:mr-0'>
              Orders
            </h1>
            <nav role='order-groupings-navigation' className='my-auto ml-8'>
              <ul className='hidden md:flex flex-row justify-center space-x-8 xl:justify-start'>
                {tabs.map(
                  (t) =>
                    t.isMain && (
                      <li key={t.name} className='flex -mb-px'>
                        <button
                          type='button'
                          onClick={() =>
                            router.replace(
                              getPathWithQuery({
                                status: t.name,
                                p: 0,
                                search: "",
                              })
                            )
                          }
                          className={`${
                            t.name === statusQuery
                              ? "border-green-default text-green-default"
                              : "border-transparent text-gray-500"
                          } border-b-2 -mb-px hover:border-green-default hover:text-green-default whitespace-nowrap py-1 px-1 font-medium`}>
                          {t.name}
                        </button>
                      </li>
                    )
                )}
              </ul>
            </nav>
            <select
              className='mt-1 block pl-3 pr-10 py-2 mr-4 ml-auto text-base border-gray-300 focus:outline-none focus:ring-green-default focus:border-green-default sm:text-sm rounded-md'
              onChange={(e) =>
                router.replace(
                  getPathWithQuery({ status: e.target.value, p: 0, search: "" })
                )
              }
              value={
                tabs.find((t) =>
                  statusQuery ? t.name === statusQuery : t.name === "Inbox"
                ).name
              }>
              {tabs.map((tab) => (
                <option key={tab.name}>{tab.name}</option>
              ))}
            </select>

            <Button
              buttonType='secondary'
              buttonSize='lg'
              href='/admin/orders/new'
              className='my-auto self-end'>
              <svg
                className='w-4 h-4 mr-2 -ml-1 mb-1'
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'>
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                />
              </svg>
              <span>Manual Order</span>
            </Button>
          </header>

          <table className='min-w-full divide-y divide-gray-200 shadow overflow-hidden border-b border-gray-200 sm:rounded-lg'>
            <thead className=' bg-gray-100 text-left text-xs leading-4 font-medium text-gray-700 uppercase tracking-wider'>
              <tr>
                <th
                  className={`px-6 py-3 hover:bg-gray-200 cursor-pointer`}
                  onClick={() => {
                    router.replace(
                      getPathWithQuery({
                        order:
                          orderBy === "asc"
                            ? "desc"
                            : orderBy === "desc"
                            ? ""
                            : "asc",
                      })
                    );
                  }}>
                  #{" "}
                  {orderBy === "asc" ? (
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      className='h-5 w-5 inline-block'
                      viewBox='0 0 20 20'
                      fill='currentColor'>
                      <path
                        fillRule='evenodd'
                        d='M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z'
                        clipRule='evenodd'
                      />
                    </svg>
                  ) : orderBy === "desc" ? (
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      className='h-5 w-5 inline-block'
                      viewBox='0 0 20 20'
                      fill='currentColor'>
                      <path
                        fillRule='evenodd'
                        d='M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z'
                        clipRule='evenodd'
                      />
                    </svg>
                  ) : (
                    ""
                  )}
                </th>
                <th className={`px-6 py-3`}>Date Created</th>
                <th className={`px-6 py-3`}>Customer</th>
                <th className={`px-6 py-3`}>Total</th>
                <th className={`px-6 py-3`}>Status</th>
                <th className={`px-6 py-3`}>Delivery Method</th>
              </tr>
            </thead>
            <tbody className='bg-white divide-y divide-gray-200 text-sm text-gray-800'>
              {loading ? (
                <tr className='hover:bg-gray-300'>
                  <td
                    className='px-6 py-4 whitespace-no-wrap leading-5 text-center'
                    colSpan={6}>
                    Loading Data...
                  </td>
                </tr>
              ) : !data || data?.orders.length === 0 ? (
                <tr>
                  <td
                    className='px-6 py-4 whitespace-no-wrap leading-5 text-center'
                    colSpan={6}>
                    Could not find orders...
                  </td>
                </tr>
              ) : (
                data?.orders.map((order) => (
                  <tr
                    key={order.id}
                    className='hover:bg-gray-300 cursor-pointer'
                    onClick={() => router.push(`/admin/orders/${order.id}`)}>
                    <td className='px-6 py-4 whitespace-no-wrap leading-5'>
                      {order.id}
                    </td>
                    <td className='px-6 py-4 whitespace-no-wrap leading-5'>
                      {DateUtil(order.date_created).format(
                        "HH:mm - DD/MM/YYYY"
                      )}
                    </td>
                    <td className='px-6 py-4 whitespace-no-wrap leading-5'>
                      {order.address.name}
                    </td>
                    <td className='px-6 py-4 whitespace-no-wrap leading-5'>
                      {currency(getOrderTotal(order as Partial<Orders>), {
                        fromCents: true,
                        symbol: "£",
                      }).format()}
                    </td>
                    <td className='px-6 py-4 whitespace-no-wrap leading-5'>
                      <span
                        className={`${
                          order.status === Order_Status_Enum.Draft
                            ? "bg-gray-200"
                            : order.status === Order_Status_Enum.New
                            ? "bg-yellow-default"
                            : order.status === Order_Status_Enum.Processing
                            ? "bg-red-default text-white"
                            : order.status === Order_Status_Enum.Fulfilled
                            ? "bg-green-default text-green-50"
                            : order.status ===
                              Order_Status_Enum.PartiallyFufilled
                            ? "bg-orange-700 text-orange-50"
                            : order.status === Order_Status_Enum.Refunded
                            ? "bg-indigo-500 text-indigo-50"
                            : "bg-gray-400"
                        } px-2 rounded-full`}>
                        {order.status}
                      </span>
                    </td>
                    <td className='px-6 py-4 whitespace-no-wrap leading-5'>
                      {order.shipping_option?.name ?? "Pickup"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </article>
        {data && (
          <nav
            className='py-3 flex items-center justify-between border-t border-gray-200 '
            aria-label='Pagination'>
            <div className='hidden sm:block'>
              <p className='text-sm text-gray-700'>
                Showing{" "}
                <span className='font-medium'>
                  {offset}
                  {` `}
                </span>
                to{" "}
                <span className='font-medium'>
                  {offset + limit}
                  {` `}
                </span>
                of{" "}
                <span className='font-medium'>
                  {data?.orders_aggregate.aggregate.count}
                  {` `}
                </span>
                results
              </p>
            </div>
            <div className='flex-1 flex justify-between sm:justify-end'>
              {paginate !== 0 && (
                <Link href={getPathWithQuery({ p: paginate - 1 })} shallow>
                  <a className='relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50'>
                    Previous
                  </a>
                </Link>
              )}
              {paginate !==
                Math.floor(data.orders_aggregate.aggregate.count / limit) && (
                <Link href={getPathWithQuery({ p: paginate + 1 })} shallow>
                  <a className='ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50'>
                    Next
                  </a>
                </Link>
              )}
            </div>
          </nav>
        )}
      </AdminLayout>
    </>
  );
};

export default AllOrders;
